// Instantiation form (docs/09 §B1) — the second step of "Create". A template's
// fields on the left, a live 16:9 preview on the right; Save persists an editable
// html_template media via /media/from-template and hands its id back so the caller
// can open it in the library.

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../../components/Modal.tsx';
import { Loading, Button, TextField } from '../../components/ui.tsx';
import { ApiError } from '../../api/client.ts';
import {
  useTemplates,
  useCreateFromTemplate,
  defaultFields,
  type TemplateFields,
} from '../../api/templates.ts';
import { TemplateFieldEditor, validateFields } from './TemplateFieldEditor.tsx';
import { TemplateLivePreview } from './TemplateLivePreview.tsx';

export function TemplateForm({
  templateId,
  onClose,
  onSaved,
}: {
  templateId: string;
  onClose: () => void;
  onSaved: (mediaId: string) => void;
}) {
  const templates = useTemplates();
  const template = templates.data?.find((t) => t.id === templateId);
  const create = useCreateFromTemplate();

  const [name, setName] = useState<string>('');
  const [values, setValues] = useState<TemplateFields | null>(null);

  // Seed name + field values from the template's defaults once it loads.
  useEffect(() => {
    if (template && values === null) {
      setValues(defaultFields(template));
      setName(template.name);
    }
  }, [template, values]);

  const fieldErrors = template && values ? validateFields(template.fields, values) : {};
  const nameError = name.trim() === '' ? 'A name is required' : undefined;
  const canSave =
    !!template && !!values && !nameError && Object.keys(fieldErrors).length === 0 && !create.isPending;

  function save() {
    if (!template || !values || !canSave) return;
    create.mutate(
      { template_id: template.id, name: name.trim(), fields: values },
      {
        onSuccess: (media) => {
          toast.success('Slide created');
          onSaved(media.id);
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not create the slide'),
      },
    );
  }

  return (
    <Modal
      title={template ? `New ${template.name.toLowerCase()} slide` : 'New slide'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={!canSave}>
            {create.isPending ? 'Saving…' : 'Save to media'}
          </Button>
        </>
      }
    >
      {!template || !values ? (
        <Loading />
      ) : (
        <div className="tpl-form">
          <div className="tpl-form-fields">
            <TextField
              label="Name"
              value={name}
              error={name && nameError ? nameError : undefined}
              onChange={(e) => setName(e.target.value)}
            />
            <TemplateFieldEditor fields={template.fields} values={values} onChange={setValues} />
          </div>
          <div className="tpl-form-preview">
            <div className="tpl-preview-label">Live preview</div>
            <TemplateLivePreview templateId={template.id} fields={values} />
          </div>
        </div>
      )}
    </Modal>
  );
}
